/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, ScanLine, CheckCircle2, AlertTriangle,
  Package, Camera, X, RotateCcw, Loader2, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * CargaRuta — Vista de checklist + escaneo QR para verificar que el montador
 * carga todos los componentes de la ruta antes de salir.
 *
 * Props:
 *  - rutaId
 *  - onBack: callback al volver
 *  - asAdmin?: si true relaja algunas restricciones (oficina viendo el estado)
 */
const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.mensaje || d?.msg || fallback;
};

export default function CargaRuta({ rutaId, onBack }) {
  const { hasPermission } = useAuth();
  const [checklist, setChecklist] = useState(null);
  const [overrideState, setOverrideState] = useState(null); // {status, ...}
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null); // {ok, mensaje, nombre, ts, tipo}
  const [busyItem, setBusyItem] = useState(null);
  const [askAuth, setAskAuth] = useState(false);
  const [motivoOverride, setMotivoOverride] = useState("");
  const [submittingOverride, setSubmittingOverride] = useState(false);
  // Iter 125 · Introducir código a mano cuando el QR no se puede leer
  const [showCodigoManual, setShowCodigoManual] = useState(false);
  const [codigoManual, setCodigoManual] = useState("");
  const [enviandoCodigo, setEnviandoCodigo] = useState(false);
  const scannerRef = useRef(null);

  const fetchChecklist = useCallback(async () => {
    try {
      const [{ data }, ruta] = await Promise.all([
        api.get(`/rutas/${rutaId}/checklist-carga`),
        api.get(`/rutas/${rutaId}/mia`).catch(() => null),
      ]);
      setChecklist(data);
      setOverrideState(ruta?.data?.carga_override || null);
    } catch (e) {
      toast.error(errMsg(e, "Error cargando checklist"));
    } finally {
      setLoading(false);
    }
  }, [rutaId]);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  // Cleanup scanner si se desmonta
  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  const procesarEscaneo = async (qrData) => {
    try {
      const { data } = await api.post(`/rutas/${rutaId}/carga/escanear`, { qr_data: qrData, metodo: "qr" });
      if (!data.ok) {
        // No pertenece / no encontrado
        setLastScan({ ok: false, mensaje: data.mensaje, ts: Date.now(), tipo: "error" });
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      } else if (data.duplicado) {
        setLastScan({ ok: true, mensaje: data.mensaje, nombre: data.componente?.nombre, ts: Date.now(), tipo: "duplicado" });
        if (navigator.vibrate) navigator.vibrate(50);
      } else {
        setLastScan({ ok: true, mensaje: data.mensaje, nombre: data.componente?.nombre, ts: Date.now(), tipo: "ok" });
        if (navigator.vibrate) navigator.vibrate(100);
        fetchChecklist();
      }
    } catch (e) {
      toast.error(errMsg(e, "Error procesando escaneo"));
    }
  };

  const startScanner = async () => {
    setScanning(true);
    setLastScan(null);
    // Esperar al siguiente tick para que el div #qr-reader exista
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("qr-reader", {
          verbose: false,
          // Iter 92 — usa el detector nativo del navegador si está disponible
          // (Chrome/Edge desktop + Android tienen BarcodeDetector). Mucho más
          // rápido y robusto con poca luz / desenfoque que el decoder JS.
          useBarCodeDetectorIfSupported: true,
        });
        scannerRef.current = scanner;
        let lastCode = null;
        let lastAt = 0;
        await scanner.start(
          // Solicita explícitamente la cámara trasera con foco continuo
          { facingMode: { exact: "environment" } },
          {
            // 25 FPS → más fluido, captura QRs en movimiento
            fps: 25,
            // QR box adaptativo: 70% del lado menor del video, máx 400px
            qrbox: (vw, vh) => {
              const min = Math.min(vw, vh);
              const size = Math.min(400, Math.floor(min * 0.7));
              return { width: size, height: size };
            },
            aspectRatio: 1.333,
            disableFlip: false,
            // Apunta a la mayor resolución que el dispositivo permita
            videoConstraints: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              focusMode: "continuous",
              advanced: [{ focusMode: "continuous" }],
            },
          },
          (decodedText) => {
            // Anti-duplicate: no escanear el mismo código en <2s
            const now = Date.now();
            if (decodedText === lastCode && now - lastAt < 2000) return;
            lastCode = decodedText;
            lastAt = now;
            procesarEscaneo(decodedText);
          },
          () => { /* ignore scan failures */ }
        );
      } catch (e) {
        // Si la cámara trasera "exact" falla (móvil con una sola cámara o
        // permiso restringido), reintentamos en modo "ideal".
        try {
          const scanner2 = new Html5Qrcode("qr-reader", {
            verbose: false,
            useBarCodeDetectorIfSupported: true,
          });
          scannerRef.current = scanner2;
          let lastCode = null;
          let lastAt = 0;
          await scanner2.start(
            { facingMode: "environment" },
            {
              fps: 20,
              qrbox: { width: 300, height: 300 },
              aspectRatio: 1.333,
              disableFlip: false,
            },
            (decodedText) => {
              const now = Date.now();
              if (decodedText === lastCode && now - lastAt < 2000) return;
              lastCode = decodedText;
              lastAt = now;
              procesarEscaneo(decodedText);
            },
            () => { /* ignore */ }
          );
        } catch (e2) {
          toast.error("No se pudo iniciar la cámara: " + (e2?.message || e?.message || "permiso denegado"));
          setScanning(false);
        }
      }
    }, 100);
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        const state = scannerRef.current.getState && scannerRef.current.getState();
        if (state === 2 /* SCANNING */) {
          await scannerRef.current.stop();
        }
        await scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (e) {
      // Cleanup silencioso: scanner ya estaba detenido o liberado
      if (process.env.NODE_ENV === "development") console.debug("stopScanner cleanup:", e?.message);
    }
    setScanning(false);
  };

  const marcarManual = async (item) => {
    setBusyItem(item.componente_id);
    try {
      const { data } = await api.post(`/rutas/${rutaId}/carga/escanear`, {
        componente_id: item.componente_id, metodo: "manual",
      });
      if (data.ok && !data.duplicado) {
        toast.success(data.mensaje);
        fetchChecklist();
      } else if (data.duplicado) {
        toast.info(data.mensaje);
      } else {
        toast.error(data.mensaje);
      }
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    } finally {
      setBusyItem(null);
    }
  };

  const desmarcar = async (item) => {
    if (!window.confirm(`Desmarcar "${item.nombre}" como cargado?`)) return;
    setBusyItem(item.componente_id);
    try {
      await api.delete(`/rutas/${rutaId}/carga/${item.componente_id}`);
      toast.success("Desmarcado");
      fetchChecklist();
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    } finally {
      setBusyItem(null);
    }
  };

  // Iter 125 · Envío de código corto tecleado a mano (fallback cuando el QR no se lee)
  const enviarCodigoManual = async () => {
    const codigo = codigoManual.trim();
    if (codigo.length < 4) { toast.error("Al menos 4 caracteres"); return; }
    setEnviandoCodigo(true);
    try {
      const { data } = await api.post(`/rutas/${rutaId}/carga/escanear`, {
        codigo_corto: codigo, metodo: "codigo_corto",
      });
      if (data.ok && !data.duplicado) {
        toast.success(data.mensaje);
        setLastScan({ tipo: "ok", mensaje: data.mensaje, nombre: data?.componente?.nombre, ts: Date.now() });
        setShowCodigoManual(false);
        setCodigoManual("");
        fetchChecklist();
      } else if (data.duplicado) {
        toast.info(data.mensaje);
        setShowCodigoManual(false);
      } else if (data.error === "codigo_ambiguo") {
        toast.error(data.mensaje);
      } else {
        toast.error(data.mensaje || "Código no reconocido");
      }
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    } finally {
      setEnviandoCodigo(false);
    }
  };

  const completarCarga = async () => {
    if (!checklist) return;
    const pend = checklist.pendientes;
    if (pend > 0) {
      // No permitimos cerrar carga incompleta a saco: hay que solicitar override
      setAskAuth(true);
      return;
    }
    try {
      await api.post(`/rutas/${rutaId}/carga/completar`);
      toast.success("Carga marcada como completada");
      fetchChecklist();
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    }
  };

  const solicitarOverride = async () => {
    if (motivoOverride.trim().length < 5) {
      toast.error("Indica el motivo (mínimo 5 caracteres)");
      return;
    }
    setSubmittingOverride(true);
    try {
      await api.post(`/rutas/${rutaId}/carga/solicitar-override`, { motivo: motivoOverride });
      toast.success("Solicitud enviada · esperando aprobación");
      setAskAuth(false);
      setMotivoOverride("");
      fetchChecklist();
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    } finally { setSubmittingOverride(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] text-zinc-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!checklist) return null;
  const { items, total, cargados, pendientes, completado, carga_completada_at } = checklist;
  const pct = total > 0 ? Math.round((cargados / total) * 100) : 0;

  // Agrupar por parada para mejor UX
  const porParada = {};
  for (const it of items) {
    const k = `${it.parada_orden || 0}__${it.espacio_nombre || "—"}`;
    if (!porParada[k]) porParada[k] = { espacio: it.espacio_nombre || "—", orden: it.parada_orden, items: [] };
    porParada[k].items.push(it);
  }
  const paradasOrdenadas = Object.values(porParada).sort((a, b) => (a.orden || 0) - (b.orden || 0));

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100" data-testid="carga-ruta-screen">
      <header className="sticky top-0 z-30 bg-[#0c0c10] border-b border-zinc-800 px-3 py-3 flex items-center gap-2">
        <button onClick={onBack} className="p-1.5 rounded hover:bg-zinc-900" data-testid="carga-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-amber-400 truncate">Carga de furgo</div>
          <div className="text-sm font-semibold truncate">{checklist.ruta_codigo}</div>
        </div>
        {completado && (
          <span className="text-[10px] uppercase tracking-wider bg-emerald-500/30 text-emerald-100 px-2 py-1 rounded">✓ Completada</span>
        )}
      </header>

      {/* Resumen + Barra progreso */}
      <div className="p-4 space-y-3">
        {total === 0 ? (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center" data-testid="carga-sin-material">
            <div className="text-3xl mb-2">🚐</div>
            <div className="text-emerald-200 font-semibold mb-1">Esta ruta no lleva material en la furgoneta</div>
            <div className="text-xs text-zinc-400">
              Retiradas y/o material que ya está en el PDV — no hay nada que escanear.
              Puedes salir directamente e iniciar la primera parada.
            </div>
          </div>
        ) : (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3" data-testid="carga-resumen">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-semibold flex items-center gap-1.5"><Package className="w-4 h-4 text-amber-400" /> Checklist</span>
            <span className="font-mono">{cargados} / {total}</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded overflow-hidden">
            <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-amber-500"} transition-all`} style={{ width: `${pct}%` }} />
          </div>
          {pendientes > 0 && (
            <div className="text-xs text-zinc-400 mt-2">⏳ {pendientes} pendiente(s)</div>
          )}
          {carga_completada_at && (
            <div className="text-xs text-emerald-300 mt-2">
              Carga cerrada el {new Date(carga_completada_at).toLocaleString("es-ES")} por {checklist.carga_completada_por}
            </div>
          )}
        </div>
        )}

        {/* Botón de escaneo / cámara */}
        {total === 0 ? null : !scanning ? (
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Button
              onClick={startScanner}
              className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold h-12 text-base"
              data-testid="carga-scan-btn">
              <ScanLine className="w-5 h-5 mr-2" /> Escanear QR
            </Button>
            <Button
              onClick={() => setShowCodigoManual(true)}
              variant="outline"
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 h-12 px-3"
              title="Introducir código a mano si el QR no se lee"
              data-testid="carga-codigo-manual-btn">
              ⌨️ Código
            </Button>
          </div>
        ) : (
          <div className="bg-zinc-950 border border-amber-500/40 rounded-xl overflow-hidden">
            <div className="relative">
              <div id="qr-reader" className="w-full" style={{ minHeight: 280 }} />
              <button
                onClick={stopScanner}
                className="absolute top-2 right-2 bg-zinc-900/80 hover:bg-zinc-900 p-2 rounded-full"
                data-testid="carga-scan-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-2 text-[11px] text-center text-zinc-400 border-t border-zinc-800">
              <Camera className="w-3 h-3 inline mr-1" /> Apunta al QR de la etiqueta del componente
            </div>
          </div>
        )}

        {/* Feedback del último escaneo */}
        {lastScan && (
          <div
            data-testid="carga-last-scan"
            className={`rounded-xl p-3 border ${
              lastScan.tipo === "ok" ? "bg-emerald-500/10 border-emerald-500/50 text-emerald-100" :
              lastScan.tipo === "duplicado" ? "bg-blue-500/10 border-blue-500/50 text-blue-100" :
              "bg-red-500/10 border-red-500/50 text-red-100"
            }`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {lastScan.tipo === "ok" ? <CheckCircle2 className="w-5 h-5" /> :
               lastScan.tipo === "duplicado" ? <RotateCcw className="w-5 h-5" /> :
               <AlertTriangle className="w-5 h-5" />}
              {lastScan.nombre || (lastScan.tipo === "error" ? "Error" : "")}
            </div>
            <div className="text-xs mt-1">{lastScan.mensaje}</div>
          </div>
        )}

        {/* Banner estado override */}
        {overrideState && (
          <div data-testid="override-state-banner"
            className={`rounded-xl p-3 border text-sm ${
              overrideState.status === "aprobada"
                ? "bg-emerald-500/10 border-emerald-500/50 text-emerald-100"
                : overrideState.status === "rechazada"
                ? "bg-red-500/10 border-red-500/50 text-red-100"
                : "bg-amber-500/10 border-amber-500/50 text-amber-100"
            }`}>
            <div className="flex items-center gap-2 font-semibold">
              <ShieldAlert className="w-4 h-4" />
              {overrideState.status === "aprobada" && "Salida autorizada · puedes arrancar"}
              {overrideState.status === "rechazada" && "Solicitud rechazada · completa la carga"}
              {overrideState.status === "solicitada" && "Esperando autorización…"}
            </div>
            <div className="text-xs mt-1.5 opacity-80">
              {overrideState.status === "solicitada" && (
                <>Solicitado a las {(overrideState.solicitado_at || "").slice(11, 16)}. Avisamos a Daniel, Sara, Elia y Fernando.</>
              )}
              {overrideState.status === "aprobada" && (
                <>{overrideState.resuelto_por} aprobó a las {(overrideState.resuelto_at || "").slice(11, 16)}.
                  {overrideState.observaciones_aprobador && <div className="mt-1">«{overrideState.observaciones_aprobador}»</div>}
                </>
              )}
              {overrideState.status === "rechazada" && (
                <>{overrideState.resuelto_por} rechazó la solicitud.
                  {overrideState.observaciones_aprobador && <div className="mt-1">«{overrideState.observaciones_aprobador}»</div>}
                </>
              )}
            </div>
          </div>
        )}

        {/* Botón completar carga */}
        {!completado && total > 0 && (
          pendientes === 0 ? (
            <Button
              onClick={completarCarga}
              className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-zinc-950"
              data-testid="carga-completar-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Confirmar carga completa
            </Button>
          ) : overrideState?.status === "solicitada" ? (
            <Button disabled className="w-full h-11 bg-amber-500/30 text-amber-100 cursor-not-allowed" data-testid="carga-completar-btn">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Esperando aprobación…
            </Button>
          ) : overrideState?.status === "aprobada" ? (
            <Button
              onClick={completarCarga}
              className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-zinc-950"
              data-testid="carga-completar-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Confirmar salida ({pendientes} pendiente(s) autorizados)
            </Button>
          ) : (
            <Button
              onClick={() => setAskAuth(true)}
              variant="outline"
              className="w-full h-11 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="carga-solicitar-override-btn">
              <ShieldAlert className="w-4 h-4 mr-2" />
              Salir sin tener todo · solicitar autorización
            </Button>
          )
        )}
      </div>

      {/* Lista checklist por parada */}
      <div className="px-4 pb-8 space-y-4" data-testid="carga-checklist-list">
        {paradasOrdenadas.length === 0 && (
          <div className="text-center text-zinc-500 text-sm py-8">No hay componentes para cargar en esta ruta.</div>
        )}
        {paradasOrdenadas.map((par, i) => (
          <div key={i} className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 px-1 flex items-center gap-2">
              <span className="bg-amber-500/30 text-amber-200 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold">{par.orden}</span>
              {par.espacio}
            </div>
            <div className="space-y-1.5">
              {par.items.map((it) => (
                <CargaItem
                  key={it.componente_id}
                  item={it}
                  busy={busyItem === it.componente_id}
                  onMarcar={hasPermission("rutas:carga_marcar_manual") ? () => marcarManual(it) : null}
                  onDesmarcar={() => desmarcar(it)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Dialog solicitar override */}
      <Dialog open={askAuth} onOpenChange={setAskAuth}>
        <DialogContent data-testid="dialog-solicitar-override">
          <DialogHeader>
            <DialogTitle>Solicitar autorización para salir sin completar</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-zinc-300">
            <div className="p-3 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Faltan <b>{pendientes}</b> de {total} componentes. La solicitud se
                envía a <b>Daniel, Sara, Elia y Fernando</b>. Podrás arrancar la
                ruta cuando alguien la apruebe.
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-400">Motivo (obligatorio)</label>
              <textarea
                value={motivoOverride}
                onChange={(e) => setMotivoOverride(e.target.value)}
                placeholder="Ej: el bulto X llega mañana y se lo dejo al cliente directamente"
                rows={3}
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm"
                data-testid="motivo-override-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAskAuth(false)}>Cancelar</Button>
            <Button onClick={solicitarOverride} disabled={submittingOverride}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
              data-testid="enviar-solicitud-override-btn">
              {submittingOverride ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
              Enviar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 125 · Introducir código de componente a mano (fallback QR ilegible) */}
      <Dialog open={showCodigoManual} onOpenChange={setShowCodigoManual}>
        <DialogContent className="bg-zinc-950 border-amber-500/40" data-testid="dialog-codigo-manual">
          <DialogHeader>
            <DialogTitle className="text-amber-200 flex items-center gap-2">
              ⌨️ Introducir código a mano
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">
              Si el QR no se lee (por curva del tubo, arrugado…), teclea el código de la etiqueta —
              son los 6 caracteres que aparecen debajo del QR.
            </p>
            <input
              type="text"
              value={codigoManual}
              onChange={(e) => setCodigoManual(e.target.value.replace(/[^0-9a-zA-Z]/g, "").toUpperCase())}
              maxLength={8}
              placeholder="A5F2C1"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && codigoManual.length >= 4) enviarCodigoManual(); }}
              className="w-full bg-zinc-900 border-2 border-amber-500/40 rounded-lg p-4 text-center text-4xl font-mono tracking-[0.4em] text-amber-100 uppercase focus:border-amber-400 outline-none"
              data-testid="input-codigo-manual"
            />
            <p className="text-xs text-zinc-500 text-center">
              Ejemplo: <span className="font-mono text-amber-300">A5F2C1</span> · 4-8 caracteres alfanuméricos
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCodigoManual(false); setCodigoManual(""); }}>
              Cancelar
            </Button>
            <Button
              onClick={enviarCodigoManual}
              disabled={enviandoCodigo || codigoManual.length < 4}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
              data-testid="btn-enviar-codigo-manual">
              {enviandoCodigo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : "✓"} Confirmar código
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CargaItem({ item, busy, onMarcar, onDesmarcar }) {
  const medidas = item.ancho_cm && item.alto_cm ? `${Math.round(item.ancho_cm)}×${Math.round(item.alto_cm)}cm` : null;
  return (
    <div
      data-testid={`carga-item-${item.componente_id}`}
      className={`flex items-center gap-3 p-2.5 rounded-lg border ${
        item.cargado
          ? "bg-emerald-500/10 border-emerald-500/40"
          : "bg-zinc-900/60 border-zinc-800"
      }`}>
      <button
        onClick={item.cargado ? onDesmarcar : (onMarcar || undefined)}
        disabled={busy || (!item.cargado && !onMarcar)}
        title={!item.cargado && !onMarcar ? "Escanea el QR o teclea el código impreso para verificar este material" : undefined}
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
          item.cargado
            ? "bg-emerald-500 text-zinc-950 hover:bg-emerald-600"
            : "bg-zinc-800 text-zinc-400 hover:bg-amber-500 hover:text-zinc-950"
        }`}
        data-testid={`carga-toggle-${item.componente_id}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
         item.cargado ? <CheckCircle2 className="w-5 h-5" /> :
         <Package className="w-5 h-5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">
          {item.nombre}
          {item.cantidad > 1 && <span className="text-xs text-zinc-400 ml-1">×{item.cantidad}</span>}
        </div>
        <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-2 gap-y-0.5">
          {item.bulto_numero && <span>📦 Bulto {item.bulto_numero}</span>}
          {medidas && <span>{medidas}</span>}
          {item.peticion_codigo && <span>{item.peticion_codigo}</span>}
        </div>
        {item.cargado && item.cargado_at && (
          <div className="text-[10px] text-emerald-300 mt-0.5">
            ✓ {new Date(item.cargado_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })} · {item.cargado_por} {item.metodo === "qr" && "📱"}
          </div>
        )}
      </div>
    </div>
  );
}
